import { _enum, array, boolean, number, object, schema, string } from "@anna-money/anna-web-lib/serializer";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";

// Sole trader decision answer entry for API
export class SoleTraderDecisionAnswerEntry extends schema(
  {
    questionId: string(),
    answerId: string(),
  },
  "SoleTraderDecisionAnswerEntry",
) {}

export enum CompanyNamePostfix {
  Ltd = "Ltd",
  Limited = "Limited",
  // AU
  PtyLtdV1 = "PTY LTD",
  PtyLtdV2 = "PTY. LTD.",
  PtyLtdV3 = "PTY. LTD",
  PtyLtdV4 = "PTY LTD.",
  PtyLimitedV1 = "PTY. LIMITED",
  PtyLimitedV2 = "PTY LIMITED",
  ProprietaryLtdV1 = "PROPRIETARY LTD.",
  ProprietaryLtdV2 = "PROPRIETARY LTD",
  ProprietaryLimited = "PROPRIETARY LIMITED",
}

export enum CompanyCountry {
  EW = "EW",
  SC = "SC",
  WA = "WA",
  NI = "NI",
  AUS = "AUS",
}

export enum AustralianState {
  AustralianCapitalTerritory = "Australian Capital Territory",
  NewSouthWales = "New South Wales",
  NorthernTerritory = "Northern Territory",
  Queensland = "Queensland",
  SouthAustralia = "South Australia",
  Tasmania = "Tasmania",
  Victoria = "Victoria",
  WesternAustralia = "Western Australia",
}

export enum GstTurnoverOptions {
  UP_TO_74_999 = "$0 - $74,999",
  FROM_75_000_TO_149_999 = "$75,000 - $149,999",
  FROM_150_000_TO_1_999_999 = "$150,000 - $1,999,999",
  FROM_2_000_000_TO_9_999_999 = "$2,000,000 - $9,999,999",
  FROM_10_000_000_TO_19_999_999 = "$10,000,000 - $19,999,999",
  OVER_20_MILLION = "$20 million and over",
}

export enum GstLodgementFrequencyOptions {
  ANNUALLY = "Annually",
  MONTHLY = "Monthly",
  QUARTERLY = "Quarterly",
}

// Type aliases for enum keys (used in forms)
export type GstLodgementFrequencyKey = keyof typeof GstLodgementFrequencyOptions;

export enum GstAccountHolderTypeCode {
  CLIENT_JOINT_OWNED_ACCOUNT = "Client Joint Owned Account",
  CLIENT_OWNED_ACCOUNT = "Client Owned Account",
  LEGAL_PRACTITIONER_OWNED_ACCOUNT = "Legal Practitioner Owned Account",
  TAX_PRACTITIONER_OWNED_ACCOUNT = "Tax Practitioner Owned Account",
}

export enum AbnApplicationReason {
  NewBusinessInAustralia = "NEW",
  ToReceivePaymentForServices = "PAY",
  PurchasedExistingBusiness = "PUR",
  ContractorSubcontractor = "CON",
  InvestmentPurposes = "INV",
  ChangeInBusinessStructure = "CBS",
}

export class CompanyData extends schema(
  {
    id: string(),
    alias: string(),
    created: string(),
    updated: string(),
    companyName: string().optional(),
    namePostfix: _enum(CompanyNamePostfix, "CompanyNamePostfix").optional(),
    pricingPlan: _enum(PricingPlan, "PricingPlan").optional(),
    country: _enum(CompanyCountry, "CompanyCountry").optional(),
    natureOfBusiness: string().optional(),
    startsTrading: string().optional(),
    managingTaxes: string().optional(),
    useVirtualOffice: boolean().optional(),
    useHomeAddressAsRegistered: boolean().optional(),
    poaFileId: string().optional(),
    letterNoObjectionFileId: string().optional(),
    registeredWithDirectorId: string().optional(),
    totalCapitalGbp: number().optional(),
    totalSharesNum: number().optional(),
    nameIdenticalAbn: string().optional(),
    needNameIdenticalAbn: boolean().optional(),
    // AU
    stateOfRegistration: _enum(AustralianState, "AustralianState").optional(),
    acn: string().optional(),
    isConductAgriculturalPropertyOperations: boolean().optional(),
    businessActivityCode: string().optional(),
    businessActivityDescription: string().optional(),
    businessActivityAddressId: string().optional(),
    // CH Verification check
    isIdvStartedForSecondaries: boolean().optional(),
    // GST Registration
    gstRegistrationRequested: boolean().optional(),
    gstStartDate: string().optional(),
    gstIsVoluntary: boolean().optional(),
    gstTurnover: _enum(GstTurnoverOptions, "GstTurnoverOptions").optional(),
    gstUseCashAccounting: boolean().optional(),
    gstLodgementFrequency: _enum(GstLodgementFrequencyOptions, "GstLodgementFrequencyOptions").optional(),
    gstImportsGoods: boolean().optional(),
    gstAccountName: string().optional(),
    gstBsb: string().optional(),
    gstAccountNumber: string().optional(),
    gstAccountHolderTypeCode: _enum(GstAccountHolderTypeCode, "GstAccountHolderTypeCode").optional(),
    // ASIC agent nomination
    auAsicAgentNominationRequested: boolean().optional(),
    asicAgentSigFileId: string().optional(),

    // Sole trader decision array
    soleTraderDecision: array(object(SoleTraderDecisionAnswerEntry)).nullish(),

    // ABN sole trader data json
    abnSoleTraderData: string().nullish(),

    // ABN application fields
    abnApplicationReason: string().nullish(),
    abnPreviousAbn: string().nullish(),
    abnExistingAbn: string().nullish(),
    abnFirstTimeBusinessOrSelfEmployed: boolean().nullish(),
    abnFirstTimeSelfEmployedOrContractor: boolean().nullish(),
    abnFirstTimeDirector: boolean().nullish(),
  },
  "CompanyData",
) {}

export class CompanyListData extends schema(
  {
    companies: array(object(CompanyData)),
  },
  "CompanyListData",
) {}

export class CompanyCreateData extends schema(
  {
    companyName: string(),
    namePostfix: _enum(CompanyNamePostfix, "CompanyNamePostfix"),
  },
  "CompanyCreateData",
) {}

export class CompanyUpdateData extends schema(
  {
    companyName: string(),
    namePostfix: _enum(CompanyNamePostfix, "CompanyNamePostfix"),
    natureOfBusiness: string().optional(),
    // GB
    startsTrading: string().optional(),
    country: _enum(CompanyCountry, "CompanyCountry").optional(),
    useVirtualOffice: boolean().optional(),
    useHomeAddressAsRegistered: boolean().optional(),
    // AU
    stateOfRegistration: _enum(AustralianState, "AustralianState").optional(),
  },
  "CompanyUpdateData",
) {}

export class CompanyStateUpdateData extends schema(
  {
    stateOfRegistration: _enum(AustralianState, "AustralianState"),
  },
  "CompanyStateUpdateData",
) {}

export class CompanyNameUpdateData extends schema(
  {
    companyName: string(),
    namePostfix: _enum(CompanyNamePostfix, "CompanyNamePostfix").optional(),
    letterNoObjectionFileId: string().optional(),
  },
  "CompanyNameUpdateData",
) {}

export class CompanyAbnCreateData extends schema(
  {
    companyName: string(),
    namePostfix: _enum(CompanyNamePostfix, "CompanyNamePostfix").optional(),
    nameIdenticalAbn: string().optional(),
  },
  "CompanyAbnCreateData",
) {}

export class CompanyPoaUpdateData extends schema(
  {
    poaFileId: string(),
  },
  "CompanyPoaUpdateData",
) {}

export class CompanyUseVirtualOfficeUpdateData extends schema(
  {
    useVirtualOffice: boolean(),
    useHomeAddressAsRegistered: boolean(),
  },
  "CompanyUseVirtualOfficeUpdateData",
) {}

export class CompanyCountryUpdateData extends schema(
  {
    country: _enum(CompanyCountry, "CompanyCountry"),
  },
  "CompanyCountryUpdateData",
) {}

export class CompanySharesUpdateData extends schema(
  {
    totalSharesNum: number(),
    totalCapitalGbp: number(),
  },
  "CompanySharesUpdateData",
) {}

export class CompanyPricingPlanUpdateData extends schema(
  {
    pricingPlan: _enum(PricingPlan, "PricingPlan").nullable(),
  },
  "CompanyPricingPlanUpdateData",
) {}

export class CompanyAbnBusinessDescriptionUpdateData extends schema(
  {
    natureOfBusiness: string().optional(),
  },
  "CompanyAbnBusinessDescriptionUpdateData",
) {}

export class CompanyAgriculturalPropertyOperationsUpdateData extends schema(
  {
    isConductAgriculturalPropertyOperations: boolean(),
  },
  "CompanyAgriculturalPropertyOperationsUpdateData",
) {}

export class CompanyMainBusinessActivityUpdateData extends schema(
  {
    businessActivityCode: string(),
    businessActivityDescription: string(),
    businessActivityAddressId: string(),
  },
  "CompanyMainBusinessActivityUpdateData",
) {}

export class CompanyAcnNumberUpdateData extends schema(
  {
    acn: string(),
  },
  "CompanyAcnNumberUpdateData",
) {}

export class CompanyAcnGSTUpdateData extends schema(
  {
    gstRegistrationRequested: boolean(),
    gstStartDate: string().optional(),
    gstIsVoluntary: boolean().optional(),
    gstTurnover: _enum(GstTurnoverOptions, "GstTurnoverOptions").optional(),
    gstUseCashAccounting: boolean().optional(),
    gstImportsGoods: boolean().optional(),
    gstLodgementFrequency: _enum(GstLodgementFrequencyOptions, "GstLodgementFrequencyOptions").optional(),
    gstAccountName: string().optional(),
    gstBsb: string().optional(),
    gstAccountNumber: string().optional(),
    gstAccountHolderTypeCode: _enum(GstAccountHolderTypeCode, "GstAccountHolderTypeCode").optional(),
  },
  "CompanyAcnGSTUpdateData",
) {}

export class CompanyAsicAgentNominationUpdateData extends schema(
  {
    auAsicAgentNominationRequested: boolean(),
    asicAgentSigFileId: string().optional(),
  },
  "CompanyAsicAgentNominationUpdateData",
) {}

export class CompanySoleTraderDecisionUpdateData extends schema(
  {
    soleTraderDecision: array(object(SoleTraderDecisionAnswerEntry)),
  },
  "CompanySoleTraderDecisionUpdateData",
) {}

export class CompanyAbnSoleTraderDataUpdateData extends schema(
  {
    abnSoleTraderData: string(),
  },
  "CompanyAbnSoleTraderDataUpdateData",
) {}

export class CompanyAbnApplicationReasonUpdateData extends schema(
  {
    abnApplicationReason: string(),
  },
  "CompanyAbnApplicationReasonUpdateData",
) {}

export class CompanyAbnExistingAbnUpdateData extends schema(
  {
    abnExistingAbn: string(),
  },
  "CompanyAbnExistingAbnUpdateData",
) {}

export class CompanyAbnFirstTimeBusinessOrSelfEmployedUpdateData extends schema(
  {
    abnFirstTimeBusinessOrSelfEmployed: boolean(),
  },
  "CompanyAbnFirstTimeBusinessOrSelfEmployedUpdateData",
) {}

export class CompanyAbnFirstTimeSelfEmployedOrContractorUpdateData extends schema(
  {
    abnFirstTimeSelfEmployedOrContractor: boolean(),
  },
  "CompanyAbnFirstTimeSelfEmployedOrContractorUpdateData",
) {}

export class CompanyAbnFirstTimeDirectorUpdateData extends schema(
  {
    abnFirstTimeDirector: boolean(),
  },
  "CompanyAbnFirstTimeDirectorUpdateData",
) {}

export type CompanyUpdateDataType =
  | CompanyUpdateData
  | CompanyNameUpdateData
  | CompanyPoaUpdateData
  | CompanyUseVirtualOfficeUpdateData
  | CompanyCountryUpdateData
  | CompanySharesUpdateData
  | CompanyPricingPlanUpdateData
  | CompanyAgriculturalPropertyOperationsUpdateData
  | CompanyMainBusinessActivityUpdateData
  | CompanyStateUpdateData
  | CompanyAbnCreateData
  | CompanyAbnBusinessDescriptionUpdateData
  | CompanyAcnGSTUpdateData
  | CompanyAcnNumberUpdateData
  | CompanyAsicAgentNominationUpdateData
  | CompanySoleTraderDecisionUpdateData
  | CompanyAbnSoleTraderDataUpdateData
  | CompanyAbnApplicationReasonUpdateData
  | CompanyAbnExistingAbnUpdateData
  | CompanyAbnFirstTimeBusinessOrSelfEmployedUpdateData
  | CompanyAbnFirstTimeSelfEmployedOrContractorUpdateData
  | CompanyAbnFirstTimeDirectorUpdateData;
